import { useMemo } from 'react';
import { SelectedDataType } from '~/_shared/components/customizeMetrics/selectedDataMetric.type';
import { type SpreadsheetColumn } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { type BoundaryGroup } from '~/boundary/boundary.types';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  type BoundaryTerritoryGroup, type BoundaryTerritoryGroupSettings,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';
import { matchBoundaryGroupDemographic } from './demographicsMetric.factory';
import { type MetricModel } from './metrics.factory';
import { areMetricsTheSame } from './metrics.helpers';
import { MetricsDataAction } from './metrics.types';

export const useBoundaryMetrics = (
  boundaryTerritoryGroup: BoundaryTerritoryGroup | undefined,
) => {
  const boundaryGroups = useBoundaryGroupsSelector();
  const spreadsheetColumns = useSpreadsheetColumns();

  const boundaryGroup = useMemo(() => boundaryGroups.find(g => g.id === boundaryTerritoryGroup?.boundaryGroupId),
    [boundaryGroups, boundaryTerritoryGroup?.boundaryGroupId]);

  const selectedMetrics: ReadonlyArray<MetricModel> = useMemo(() => {
    if (!boundaryTerritoryGroup || !boundaryGroup) {
      return [];
    }

    const metricsFromFill: MetricModel[] = [
      ...getFillDemographics(boundaryTerritoryGroup.settings, boundaryGroup),
      ...getFillNumericMetrics(boundaryTerritoryGroup.settings, spreadsheetColumns),
      ...getFillGroupMetrics(boundaryTerritoryGroup.settings, spreadsheetColumns),
    ].reduce((filteredMetrics, metric) => {
      if (filteredMetrics.every(filteredMetric => !areMetricsTheSame(filteredMetric, metric))) {
        filteredMetrics.push(metric);
      }

      return filteredMetrics;
    }, [] as MetricModel[]);

    const savedMetricsWithoutFillMetrics = boundaryTerritoryGroup.settings.metrics
      .map(metric => getBestDemographic(metric, boundaryGroup))
      .filter(savedMetric => !metricsFromFill.some(fillMetric => areMetricsTheSame(savedMetric, fillMetric)));

    return [
      ...metricsFromFill,
      ...savedMetricsWithoutFillMetrics,
    ];
  }, [boundaryGroup, boundaryTerritoryGroup, spreadsheetColumns]);

  return selectedMetrics;
};

type BoundarySettingsSubset = Pick<BoundaryTerritoryGroupSettings, 'boundaryTerritoryType' | 'bucketColumnId' | 'demographicId'>;

const getFillDemographics = (btgSettings: BoundarySettingsSubset, boundaryGroup: BoundaryGroup) => {
  if (btgSettings.boundaryTerritoryType !== BoundaryTerritoryType.Demographic) {
    return [];
  }

  const demographicFromFill = boundaryGroup.demographics
    .find(d => d.id === btgSettings.demographicId);

  if (demographicFromFill) {
    return [{
      type: SelectedDataType.Demographic,
      demographic: demographicFromFill,
    } as const];
  }

  return [];
};

const getFillNumericMetrics = (btgSettings: BoundarySettingsSubset,
  spreadsheetColumns: ReadonlyArray<SpreadsheetColumn>) => {
  if (btgSettings.boundaryTerritoryType !== BoundaryTerritoryType.Numeric) {
    return [];
  }

  const spreadsheetColumn = spreadsheetColumns.find(c => c.id === btgSettings.bucketColumnId);
  if (!spreadsheetColumn) {
    return [];
  }

  const numericActions = [MetricsDataAction.SUM, MetricsDataAction.AVERAGE, MetricsDataAction.HIGH_VALUE,
    MetricsDataAction.LOW_VALUE];
  return numericActions.map(dataAction => ({
    type: SelectedDataType.SpreadsheetColumn,
    spreadsheetColumn,
    dataAction,
  } as const));
};

const getFillGroupMetrics = (btgSettings: BoundarySettingsSubset,
  spreadsheetColumns: ReadonlyArray<SpreadsheetColumn>) => {
  if (btgSettings.boundaryTerritoryType !== BoundaryTerritoryType.Groups) {
    return [];
  }

  const spreadsheetColumn = spreadsheetColumns.find(c => c.id === btgSettings.bucketColumnId);
  if (!spreadsheetColumn) {
    return [];
  }

  return [{
    type: SelectedDataType.SpreadsheetColumn,
    spreadsheetColumn,
    dataAction: MetricsDataAction.GROUP_COUNT,
  } as const];
};

const getBestDemographic = (metric: MetricModel, boundaryGroup: BoundaryGroup) => {
  if (metric.type === SelectedDataType.Demographic) {
    const nativeDemographic = boundaryGroup.demographics
      .find(demographic => matchBoundaryGroupDemographic(demographic.id, metric.demographic.id));

    if (nativeDemographic) {
      return {
        ...metric,
        demographic: nativeDemographic,
      };
    }
  }

  return metric;
};
